import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, ChevronDown, ChevronUp } from "lucide-react";

export const MODULE_OPTIONS = [
  { key: "today", label: "Home" },
  { key: "contacts", label: "Contacts" },
  { key: "projects", label: "Projects" },
  { key: "finance", label: "Finance" },
  { key: "calendar", label: "Calendar" },
  { key: "tasks", label: "Tasks" },
  { key: "documents", label: "Documents" },
  { key: "campaigns", label: "Campaigns" },
  { key: "news", label: "News" },
  { key: "analytics", label: "Analytics" },
];

const STARTER_MODULES = ["today", "projects", "tasks", "calendar", "documents"];
const randomPassword = () => `halo-${Math.random().toString(36).slice(2, 8)}${Math.floor(Math.random() * 90 + 10)}`;

function ModulePicker({ value, onChange, idPrefix }) {
  const toggle = (key) =>
    onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {MODULE_OPTIONS.map((m) => (
        <label key={m.key} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            data-testid={`${idPrefix}-module-${m.key}`}
            checked={value.includes(m.key)}
            onCheckedChange={() => toggle(m.key)}
          />
          <span>{m.label}</span>
        </label>
      ))}
    </div>
  );
}

function MemberRow({ member, ownerId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState(member.allowed_modules || []);
  const [newPassword, setNewPassword] = useState("");
  const isOwner = member.id === ownerId;

  const patch = async (body, message) => {
    try {
      await api.put(`/team/${member.id}`, body);
      toast.success(message);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update that member");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${member.name} from this workspace?`)) return;
    try {
      await api.delete(`/team/${member.id}`);
      toast.success(`${member.name} removed`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove that member");
    }
  };

  return (
    <div className="border border-border rounded-lg p-3" data-testid={`team-row-${member.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-2">
            {member.name}
            <Badge variant={member.role === "admin" ? "default" : "secondary"}>
              {isOwner ? "owner" : member.role}
            </Badge>
            {member.must_change_password && (
              <Badge variant="outline" data-testid={`team-pending-${member.id}`}>password reset pending</Badge>
            )}
            {member.is_active === false && <Badge variant="destructive">deactivated</Badge>}
          </div>
          <div className="text-xs text-muted-foreground truncate">{member.email}</div>
        </div>
        {!isOwner && (
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              Active
              <Switch
                data-testid={`team-active-${member.id}`}
                checked={member.is_active !== false}
                onCheckedChange={(v) => patch({ is_active: v }, v ? "Member reactivated" : "Member deactivated")}
              />
            </div>
            <Button variant="ghost" size="sm" data-testid={`team-expand-${member.id}`}
                    onClick={() => setOpen((o) => !o)}>
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span className="ml-1">Access</span>
            </Button>
            <Button variant="ghost" size="sm" data-testid={`team-remove-${member.id}`} onClick={remove}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {open && !isOwner && (
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs">Modules this person can open</Label>
            <div className="mt-2">
              <ModulePicker value={modules} onChange={setModules} idPrefix={`team-${member.id}`} />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" data-testid={`team-save-modules-${member.id}`}
                      onClick={() => patch({ allowed_modules: modules }, "Access updated — they'll be signed out to pick it up")}>
                Save access
              </Button>
              <Button size="sm" variant="outline" data-testid={`team-role-${member.id}`}
                      onClick={() => {
                        const next = member.role === "admin" ? "member" : "admin";
                        if (!window.confirm(`Make ${member.name} ${next === "admin" ? "an admin (full access to everything, including settings and data deletion)" : "a member"}?`)) return;
                        patch({ role: next }, `Now ${next === "admin" ? "an admin" : "a member"}`);
                      }}>
                Make {member.role === "admin" ? "member" : "admin"}
              </Button>
            </div>
          </div>
          <div className="flex items-end gap-2 max-w-md">
            <div className="flex-1">
              <Label className="text-xs">Set a new temporary password</Label>
              <Input data-testid={`team-password-${member.id}`} value={newPassword}
                     placeholder="min 8 characters"
                     onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <Button size="sm" variant="outline" data-testid={`team-reset-password-${member.id}`}
                    disabled={newPassword.length < 8}
                    onClick={async () => {
                      await patch({ password: newPassword }, "Password reset — share it with them");
                      setNewPassword("");
                    }}>
              <KeyRound className="w-4 h-4 mr-1" /> Reset
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function TeamPanel({ ownerId }) {
  const [team, setTeam] = useState([]);
  const [draft, setDraft] = useState({
    name: "", email: "", password: randomPassword(), role: "member",
    allowed_modules: STARTER_MODULES,
  });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/team")
      .then(({ data }) => setTeam(data))
      .catch((err) => console.error("Could not load the team", err));
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    try {
      await api.post("/team", draft);
      toast.success(`${draft.name} added — share the temporary password with them`);
      setOpen(false);
      setDraft({ name: "", email: "", password: randomPassword(), role: "member",
                 allowed_modules: STARTER_MODULES });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add that person");
    }
  };

  return (
    <Card className="studio-card" data-testid="team-panel">
      <CardHeader className="flex-row items-center gap-3">
        <div>
          <CardTitle className="text-base">Team</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Admins can do everything. Members only see the modules you tick, and can never reach
            settings, imports, exports or data deletion.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="ml-auto shrink-0" data-testid="team-invite-open">
              <UserPlus className="w-4 h-4 mr-2" /> Invite employee
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Invite an employee</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input data-testid="team-invite-name" value={draft.name}
                       onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Work email</Label>
                <Input data-testid="team-invite-email" type="email" value={draft.email}
                       onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Temporary password (they must change it on first sign-in)</Label>
                <div className="flex gap-2">
                  <Input data-testid="team-invite-password" value={draft.password}
                         onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
                  <Button variant="outline" data-testid="team-invite-generate"
                          onClick={() => setDraft({ ...draft, password: randomPassword() })}>
                    New
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Modules they can open</Label>
                <div className="mt-2">
                  <ModulePicker value={draft.allowed_modules} idPrefix="team-invite"
                                onChange={(v) => setDraft({ ...draft, allowed_modules: v })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="team-invite-submit" onClick={invite}
                      disabled={!draft.name || !draft.email || draft.password.length < 8}>
                Add to workspace
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent className="space-y-2">
        {team.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="team-empty">
            Just you so far.
          </p>
        )}
        {team.map((m) => (
          <MemberRow key={m.id} member={m} ownerId={ownerId} onChanged={load} />
        ))}
      </CardContent>
    </Card>
  );
}
